import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AppUser, CompanyInfo, ContractedCompany, InventoryItem, PrintMode, ServiceTicket, StockMovement, TicketStatus } from './types';
import {
  getStoredTickets,
  saveTickets,
  getStoredCompanyInfo,
  saveCompanyInfo,
  getStoredDeviceTypes,
  saveDeviceTypes,
  getStoredDeviceBrands,
  saveDeviceBrands,
  getStoredInventory,
  saveInventory,
  getStoredInventoryCategories,
  saveInventoryCategories,
  getStoredStockMovements,
  saveStockMovements,
  getStoredContractedCompanies,
  saveContractedCompanies,
  resetToDefaults,
  fetchServerData,
  checkServerConnection,
  getCurrentAuthVersion,
  setCurrentAuthVersion,
  getCurrentUser,
  setCurrentUser,
  clearAuthSession,
  clearSensitiveLocalCache,
  validateCurrentSession,
  generateNextTicketId,
  fetchLiveExchangeRate,
  getStoredExchangeRate,
  formatPhoneDisplay,
  consumeItemStockLowestCostFirst,
  restoreItemStockLots,
} from './utils/storage';
import { WifiOff, RefreshCw, AlertTriangle, X } from 'lucide-react';
import { defaultCompanyInfo, defaultDeviceTypes, defaultDeviceBrands, defaultInventoryCategories } from './data/initialData';
import { Header } from './components/Header';
import { DashboardView } from './components/DashboardView';
import { HistoryView } from './components/HistoryView';
import { HiddenArchiveView } from './components/HiddenArchiveView';
import { ReportsView } from './components/ReportsView';
import { InventoryView } from './components/InventoryView';
import { NewTicketModal } from './components/NewTicketModal';
import { TicketDetailModal } from './components/TicketDetailModal';
import { PrintTicketModal } from './components/PrintTicketModal';
import { CompanySettingsModal } from './components/CompanySettingsModal';
import { ManageDeviceTypesModal } from './components/ManageDeviceTypesModal';
import { SmsModal } from './components/SmsModal';
import { LoginScreen } from './components/LoginScreen';
import { CustomerTrackingView } from './components/CustomerTrackingView';
import { triggerAutoSmsIfEnabled } from './utils/smsService';

function checkIsTrackingUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.toLowerCase();
  const search = window.location.search.toLowerCase();
  const hash = window.location.hash.toLowerCase();
  return (
    path.startsWith('/takip') ||
    path.startsWith('/sorgula') ||
    path.startsWith('/cihaz-takip') ||
    path.startsWith('/durum') ||
    hash.includes('takip') ||
    hash.includes('sorgula') ||
    search.includes('sayfa=takip') ||
    search.includes('sayfa=sorgula') ||
    search.includes('mode=track') ||
    search.includes('mode=takip') ||
    search.includes('takip=') ||
    search.includes('ticket=')
  );
}

export default function App() {
  // Birden fazla GET üst üste binerse yalnızca en son başlayan isteğin sonucu ekrana uygulanır.
  // Böylece eski bir ağ cevabı daha geç gelip daha yeni veriyi geri çeviremez.
  const latestLoadRequestRef = useRef(0);
  const [isTrackingRoute, setIsTrackingRoute] = useState<boolean>(() => checkIsTrackingUrl());
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return sessionStorage.getItem('ts_auth') === 'true' || localStorage.getItem('ts_auth_remember') === 'true';
  });
  const [currentUser, setCurrentUserState] = useState<AppUser | null>(() => getCurrentUser());
  const isTechnician = currentUser?.role === 'technician';
  const isLimitedTechnician = currentUser?.role === 'limited_technician';
  const isAdmin = currentUser?.role === 'admin' || (!currentUser && !isTechnician && !isLimitedTechnician);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string>('');
  const [tickets, setTickets] = useState<ServiceTicket[]>(() => getStoredTickets());
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(getStoredCompanyInfo());
  const [deviceTypes, setDeviceTypes] = useState<string[]>(getStoredDeviceTypes());
  const [deviceBrands, setDeviceBrands] = useState<string[]>(getStoredDeviceBrands());
  const [inventory, setInventory] = useState<InventoryItem[]>(getStoredInventory());
  const [inventoryCategories, setInventoryCategories] = useState<string[]>(getStoredInventoryCategories());
  const [stockMovements, setStockMovements] = useState<StockMovement[]>(getStoredStockMovements());
  const [contractedCompanies, setContractedCompanies] = useState<ContractedCompany[]>(() => getStoredContractedCompanies());
  const [activeTab, setActiveTab] = useState<'dashboard' | 'history' | 'inventory' | 'reports' | 'archive'>('dashboard');
  const [isSyncing, setIsSyncing] = useState(false);

  // Modal States
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<ServiceTicket | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDeviceTypesModalOpen, setIsDeviceTypesModalOpen] = useState(false);

  // SMS Modal State
  const [smsTicket, setSmsTicket] = useState<ServiceTicket | null>(null);
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);

  // Print Modal state
  const [printTicket, setPrintTicket] = useState<ServiceTicket | null>(null);
  const [printMode, setPrintMode] = useState<PrintMode>('intake');
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isOfflineAlertOpen, setIsOfflineAlertOpen] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);

  // Realtime notification when new tickets arrive in "MusteriOnayi" (Approval Pending)
  const [approvalAlertTicket, setApprovalAlertTicket] = useState<ServiceTicket | null>(null);
  const knownApprovalIdsRef = React.useRef<Set<string>>(new Set());
  const isFirstApprovalCheckRef = React.useRef<boolean>(true);

  const playApprovalChime = () => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, now); // D5
      osc.frequency.setValueAtTime(880.0, now + 0.12); // A5
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.4);
    } catch {
      // Audio context might be restricted before interaction
    }
  };

  useEffect(() => {
    // Only limited technician role receives approval waiting notifications
    if (!isLimitedTechnician) {
      setApprovalAlertTicket(null);
      return;
    }

    const currentApprovalTickets = tickets.filter(t => t.status === 'MusteriOnayi');
    const currentIds = new Set(currentApprovalTickets.map(t => t.id));

    if (isFirstApprovalCheckRef.current) {
      knownApprovalIdsRef.current = currentIds;
      isFirstApprovalCheckRef.current = false;
      return;
    }

    // Detect newly arrived tickets in MusteriOnayi
    const newApprovalTickets = currentApprovalTickets.filter(t => !knownApprovalIdsRef.current.has(t.id));
    if (newApprovalTickets.length > 0) {
      const latest = newApprovalTickets[0];
      setApprovalAlertTicket(latest);
      playApprovalChime();
    }

    knownApprovalIdsRef.current = currentIds;
  }, [tickets, isLimitedTechnician]);

  // Listen to browser URL changes (popstate & hashchange) for direct tracking link
  useEffect(() => {
    const handleUrlChange = () => {
      setIsTrackingRoute(checkIsTrackingUrl());
    };
    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('hashchange', handleUrlChange);
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('hashchange', handleUrlChange);
    };
  }, []);

  // Function to load latest data from server & verify session token
  const loadLatestData = async () => {
    const requestId = ++latestLoadRequestRef.current;
    setIsSyncing(true);
    try {
      const serverData = await fetchServerData();
      // Bu istekten sonra başka bir yükleme başladıysa bu cevap artık bayattır.
      if (requestId !== latestLoadRequestRef.current) return;
      const authFlagStillPresent =
        sessionStorage.getItem('ts_auth') === 'true' ||
        localStorage.getItem('ts_auth_remember') === 'true';
      if (!serverData && !authFlagStillPresent) {
        setIsAuthenticated(false);
        setCurrentUser(null);
        setCurrentUserState(null);
        setSessionExpiredMessage('Oturumunuz sona erdi. Lütfen tekrar giriş yapınız.');
      }
      if (serverData?.tickets) {
        setTickets(serverData.tickets);
      } else {
        setTickets(getStoredTickets());
      }
      if (serverData?.company) {
        setCompanyInfo(serverData.company);
      } else {
        setCompanyInfo(getStoredCompanyInfo());
      }
      if (serverData?.deviceTypes && serverData.deviceTypes.length > 0) {
        setDeviceTypes(serverData.deviceTypes);
      } else {
        setDeviceTypes(getStoredDeviceTypes());
      }
      if (serverData?.deviceBrands && serverData.deviceBrands.length > 0) {
        setDeviceBrands(serverData.deviceBrands);
      } else {
        setDeviceBrands(getStoredDeviceBrands());
      }
      if (serverData?.inventory) {
        setInventory(serverData.inventory);
      } else {
        setInventory(getStoredInventory());
      }
      if (serverData?.inventoryCategories && serverData.inventoryCategories.length > 0) {
        setInventoryCategories(serverData.inventoryCategories);
      } else {
        setInventoryCategories(getStoredInventoryCategories());
      }
      if (serverData?.stockMovements) {
        setStockMovements(serverData.stockMovements);
      } else {
        setStockMovements(getStoredStockMovements());
      }
      if (serverData?.contractedCompanies) {
        setContractedCompanies(serverData.contractedCompanies);
      } else {
        setContractedCompanies(getStoredContractedCompanies());
      }

      // Check session validity across devices and tabs
      const hasActiveAuth =
        sessionStorage.getItem('ts_auth') === 'true' ||
        localStorage.getItem('ts_auth_remember') === 'true';

      if (hasActiveAuth) {
        const sessionCheck = validateCurrentSession(serverData);
        if (!sessionCheck.isValid) {
          clearAuthSession();
          setIsAuthenticated(false);
          setCurrentUser(null);
          setCurrentUserState(null);
          setSessionExpiredMessage(
            sessionCheck.reason ||
            '⚠️ Şifreniz veya kullanıcı yetkileriniz değiştirildiği için bu cihazdaki oturumunuz güvenlik amacıyla sonlandırıldı. Lütfen yeni şifrenizle tekrar giriş yapınız.'
          );
        }
      }
    } catch (err: any) {
      if (err?.message === 'SESSION_UNAUTHORIZED') {
        clearAuthSession();
        setIsAuthenticated(false);
        setCurrentUser(null);
        setCurrentUserState(null);
        setSessionExpiredMessage('Oturumunuz sona erdi. Lütfen tekrar giriş yapınız.');
      } else if (err?.message === 'MYSQL_REQUIRED') {
        // SQL-only modda bağlantı bilgileri silinmişse eski tarayıcı verisini
        // bağlıymış gibi göstermeyiz. Kurulum ekranına otomatik dönülür.
        clearSensitiveLocalCache();
        localStorage.removeItem('tekservis_mysql_config');
        clearAuthSession();
        setTickets([]);
        setCompanyInfo(defaultCompanyInfo);
        setDeviceTypes(defaultDeviceTypes);
        setDeviceBrands(defaultDeviceBrands);
        setInventory([]);
        setInventoryCategories(defaultInventoryCategories);
        setStockMovements([]);
        setContractedCompanies([]);
        setCurrentUser(null);
        setCurrentUserState(null);
        setIsAuthenticated(false);
        setSessionExpiredMessage('MySQL bağlantı bilgileri bulunamadı veya bağlantı kesildi. Lütfen veritabanını yeniden yapılandırınız.');
      } else {
        // Kısa süreli sürüm yükleme/ağ kesintisinde oturumu ilk hatada bozma;
        // ancak kullanıcıya bağlantının aktif olmadığını açıkça göster.
        setIsOfflineAlertOpen(true);
      }
    } finally {
      if (requestId === latestLoadRequestRef.current) {
        setIsSyncing(false);
      }
    }
  };

  // Korumalı veriyi yalnız sunucu girişi başarıyla tamamlandıktan sonra çek.
  // Böylece ilk açılışta oluşan 401 isteği, aynı anda tamamlanan login oturumunu silemez.
  useEffect(() => {
    if (!isAuthenticated) return;

    loadLatestData();

    // Dengeli senkron: odaktayken 8 sn'de bir; sekmeye dönünce anında yenilenir.
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadLatestData();
    }, 8000);

    const handleFocus = () => {
      if (isAuthenticated) loadLatestData();
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (
        isAuthenticated && (
          !e.key ||
          e.key.startsWith('ts_') ||
          e.key === 'tekservis_contracted_companies' ||
          e.key === 'service_custom_label_config_v2'
        )
      ) {
        loadLatestData();
      }
    };

    const handleDatabaseChanged = () => {
      // MySQL bağlantısı ayarlardan değiştiğinde oturumu kapatmadan yeni SQL
      // verisini hemen yükle. Diğer cihazlar aynı değişimi 8 sn'lik kontrolde algılar.
      if (isAuthenticated) loadLatestData();
    };

    // Saatlik Otomatik Dolar Kuru Kontrolü & Yenilemesi
    const checkAndRefreshExchangeRate = async () => {
      try {
        const storedRate = getStoredExchangeRate();
        const lastUpdated = storedRate?.updatedAt ? new Date(storedRate.updatedAt).getTime() : 0;
        // Son güncellemeden bu yana 1 saat (3600 sn) geçmişse veya hiç çekilmemişse otomatik tazele
        if (!lastUpdated || Date.now() - lastUpdated >= 60 * 60 * 1000) {
          await fetchLiveExchangeRate(true);
        }
      } catch (_) {}
    };

    checkAndRefreshExchangeRate();

    // Her 1 saatte bir (3600000 ms) otomatik dolar kurunu yenile
    const hourlyExchangeRateInterval = setInterval(() => {
      fetchLiveExchangeRate(true).catch(() => {});
    }, 60 * 60 * 1000);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('tekservis-database-changed', handleDatabaseChanged);
    return () => {
      clearInterval(interval);
      clearInterval(hourlyExchangeRateInterval);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tekservis-database-changed', handleDatabaseChanged);
    };
  }, [isAuthenticated]);

  // Save tickets helper
  const handleUpdateTickets = (newTickets: ServiceTicket[], replaceAll = true) => {
    // Gizlenmiş (soft-delete) kayıtlar görünür ekranlardan gelmeyebilir.
    // Bu nedenle güncelleme sırasında daha önce gizlenmiş kayıtları asla düşürme.
    const incomingIds = new Set(newTickets.map(t => t.id));
    const preservedHidden = tickets.filter(t => t.isDeleted && !incomingIds.has(t.id));
    const mergedTickets = [...newTickets, ...preservedHidden];
    setTickets(mergedTickets);
    if (replaceAll) {
      saveTickets(mergedTickets, true);
    } else {
      const currentMap = new Map(tickets.map(t => [t.id, t]));
      const changedTickets = mergedTickets.filter(t => {
        const old = currentMap.get(t.id);
        if (!old) return true;
        return String(t.updatedAt || t.lastUpdatedAt || '') !== String(old.updatedAt || old.lastUpdatedAt || '');
      });
      if (changedTickets.length > 0) saveTickets(changedTickets, false);
    }
  };

  // Save inventory helper
  const handleUpdateInventory = (newInventory: InventoryItem[]) => {
    setInventory(newInventory);
    saveInventory(newInventory);
  };

  // Save inventory categories helper
  const handleUpdateInventoryCategories = (newCategories: string[]) => {
    setInventoryCategories(newCategories);
    saveInventoryCategories(newCategories);
  };

  // Save stock movements helper
  const handleUpdateStockMovements = (newMovements: StockMovement[]) => {
    setStockMovements(newMovements);
    saveStockMovements(newMovements);
  };

  // Save contracted companies helper
  const handleUpdateContractedCompanies = (newCompanies: ContractedCompany[]) => {
    setContractedCompanies(newCompanies);
    saveContractedCompanies(newCompanies);
  };

  // Create Ticket
  const handleCreateTicket = (ticket: ServiceTicket) => {
    const currentUserName = currentUser?.name || currentUser?.fullName || currentUser?.username || 'Yönetici';
    const currentUsername = currentUser?.username || 'admin';
    const nowISO = new Date().toISOString();

    let finalTicket: ServiceTicket = {
      ...ticket,
      createdByUser: ticket.createdByUser || currentUsername,
      createdByName: ticket.createdByName || currentUserName,
      lastUpdatedByUser: currentUsername,
      lastUpdatedByName: currentUserName,
      lastUpdatedAt: nowISO,
    };
    // Çakışma kontrolü: Eğer aynı anda başka bir cihaz bu ID'yi kullandıysa bir sonrakini ata
    if (tickets.some(t => t.id === ticket.id)) {
      const nextId = generateNextTicketId(tickets, companyInfo?.name);
      finalTicket = { ...finalTicket, id: nextId };
    }
    const updated = [finalTicket, ...tickets];
    handleUpdateTickets(updated, false);

    // Otomatik SMS Bildirimi (Açık ise)
    triggerAutoSmsIfEnabled('ticketCreated', finalTicket, companyInfo).catch(err => {
      console.error('Kayıt SMS gönderim hatası:', err);
    });
  };

  // Save/Update Ticket with automated stock deduction & movement tracking
  const handleSaveTicket = (updatedTicket: ServiceTicket) => {
    const oldTicket = tickets.find(t => t.id === updatedTicket.id);
    const currentUserName = currentUser?.name || currentUser?.fullName || currentUser?.username || 'Sistem Yöneticisi';
    const currentUsername = currentUser?.username || 'admin';
    const nowISO = new Date().toISOString();

    // Ensure lastUpdated personnel and delivery personnel are automatically filled
    const enrichedTicket: ServiceTicket = {
      ...updatedTicket,
      lastUpdatedByUser: currentUsername,
      lastUpdatedByName: currentUserName,
      lastUpdatedAt: nowISO,
      deliveredByUser:
        updatedTicket.status === 'TeslimEdildi' || updatedTicket.status === 'IadeEdildi'
          ? updatedTicket.deliveredByUser || currentUsername
          : updatedTicket.deliveredByUser,
      deliveredByName:
        updatedTicket.status === 'TeslimEdildi' || updatedTicket.status === 'IadeEdildi'
          ? updatedTicket.deliveredByName || currentUserName
          : updatedTicket.deliveredByName,
      deliveredAt:
        updatedTicket.status === 'TeslimEdildi' || updatedTicket.status === 'IadeEdildi'
          ? updatedTicket.deliveredAt || nowISO
          : updatedTicket.deliveredAt,
    };

    // Otomatik SMS Bildirimi (Durum Değişikliği ve Otomatik SMS Açık ise)
    if (oldTicket && oldTicket.status !== enrichedTicket.status) {
      if (enrichedTicket.status === 'MusteriOnayi') {
        triggerAutoSmsIfEnabled('ticketApprovalWaiting', enrichedTicket, companyInfo).catch(err => {
          console.error('Onay SMS gönderim hatası:', err);
        });
      } else if (enrichedTicket.status === 'Hazir') {
        triggerAutoSmsIfEnabled('ticketReady', enrichedTicket, companyInfo).catch(err => {
          console.error('Hazır SMS gönderim hatası:', err);
        });
      } else if (enrichedTicket.status === 'TeslimEdildi') {
        triggerAutoSmsIfEnabled('ticketDelivered', enrichedTicket, companyInfo).catch(err => {
          console.error('Teslim SMS gönderim hatası:', err);
        });
      }
    }

    // Calculate stock changes for parts linked to inventory
    let updatedInventory = [...inventory];
    let newMovements: StockMovement[] = [];

    const oldPartsMap = new Map<string, number>();
    if (oldTicket && oldTicket.parts) {
      oldTicket.parts.forEach(p => {
        if (p.inventoryItemId) {
          const current = oldPartsMap.get(p.inventoryItemId) || 0;
          oldPartsMap.set(p.inventoryItemId, current + (p.quantity || 1));
        }
      });
    }

    const newPartsMap = new Map<string, { qty: number; name: string }>();
    if (updatedTicket.parts) {
      updatedTicket.parts.forEach(p => {
        if (p.inventoryItemId) {
          const current = newPartsMap.get(p.inventoryItemId) || { qty: 0, name: p.name };
          newPartsMap.set(p.inventoryItemId, { qty: current.qty + (p.quantity || 1), name: p.name });
        }
      });
    }

    // Process all inventory items touched
    const allTouchedItemIds = new Set([...oldPartsMap.keys(), ...newPartsMap.keys()]);
    let inventoryChanged = false;

    allTouchedItemIds.forEach(invId => {
      const oldQty = oldPartsMap.get(invId) || 0;
      const newInfo = newPartsMap.get(invId);
      const newQty = newInfo ? newInfo.qty : 0;
      const delta = newQty - oldQty;

      if (delta !== 0) {
        const invIndex = updatedInventory.findIndex(i => i.id === invId);
        if (invIndex !== -1) {
          const item = updatedInventory[invIndex];
          const prevItemQty = item.quantity || 0;
          let updatedItem: InventoryItem;

          if (delta > 0) {
            const result = consumeItemStockLowestCostFirst(item, delta);
            updatedItem = result.updatedItem;
          } else {
            updatedItem = restoreItemStockLots(item, Math.abs(delta));
          }

          updatedInventory[invIndex] = updatedItem;
          inventoryChanged = true;

          const movement: StockMovement = {
            id: 'mov_' + Date.now() + '_' + Math.random().toString(36).substring(2, 5),
            itemId: item.id,
            itemName: item.name,
            type: delta > 0 ? 'out' : 'in',
            quantity: Math.abs(delta),
            previousQuantity: prevItemQty,
            newQuantity: updatedItem.quantity,
            note:
              delta > 0
                ? `${updatedTicket.id} nolu serviste kullanıldı (${updatedTicket.customerName})`
                : `${updatedTicket.id} nolu servisten çıkarıldı / iade edildi`,
            ticketId: updatedTicket.id,
            date: nowISO,
          };
          newMovements.push(movement);
        }
      }
    });

    if (inventoryChanged) {
      setInventory(updatedInventory);
      saveInventory(updatedInventory);
    }

    if (newMovements.length > 0) {
      const updatedMovements = [...newMovements, ...stockMovements];
      setStockMovements(updatedMovements);
      saveStockMovements(updatedMovements);
    }

    const updated = tickets.map(t => (t.id === enrichedTicket.id ? enrichedTicket : t));
    handleUpdateTickets(updated, false);
    if (selectedTicket && selectedTicket.id === enrichedTicket.id) {
      setSelectedTicket(enrichedTicket);
    }
  };

  // Delete Ticket (SOFT DELETE: SQL kaydı korunur, sadece sistemden gizlenir)
  const handleDeleteTicket = (id: string) => {
    if (!isAdmin) {
      alert('Sadece yönetici (admin) hesaplarının servis formunu silme yetkisi bulunmaktadır.');
      return;
    }

    const nowISO = new Date().toISOString();
    const currentUserName = currentUser?.name || currentUser?.fullName || currentUser?.username || 'Sistem Yöneticisi';
    const currentUsername = currentUser?.username || 'admin';
    const updated = tickets.map(t =>
      t.id === id
        ? {
            ...t,
            isDeleted: true,
            deletedAt: nowISO,
            deletedByUser: currentUsername,
            deletedByName: currentUserName,
            updatedAt: nowISO,
          }
        : t
    );

    // Form sadece gizlenir. Parça/stok hareketleri geri alınmaz; geçmiş ve mali kayıt korunur.
    handleUpdateTickets(updated, true);
    setIsDetailModalOpen(false);
    setSelectedTicket(null);
  };

  // Restore hidden ticket: SQL kaydı korunur, form tekrar normal listelerde görünür.
  const handleRestoreTicket = (ticket: ServiceTicket) => {
    if (!isAdmin) {
      alert('Sadece yönetici (admin) hesapları gizli formları tekrar görünür yapabilir.');
      return;
    }

    const nowISO = new Date().toISOString();
    const restored = {
      ...ticket,
      isDeleted: false,
      deletedAt: undefined,
      deletedByUser: undefined,
      deletedByName: undefined,
      updatedAt: nowISO,
    };

    const updated = tickets.map(t => (t.id === ticket.id ? restored : t));
    handleUpdateTickets(updated, true);

    if (selectedTicket?.id === ticket.id) {
      setSelectedTicket(restored);
    }
  };

  // Quick Status Update from table row
  const handleQuickUpdateStatus = (ticketId: string, newStatus: TicketStatus) => {
    const nowISO = new Date().toISOString();
    const currentTicket = tickets.find(t => t.id === ticketId);
    const oldStatus = currentTicket?.status;
    const currentUserName = currentUser?.name || currentUser?.fullName || currentUser?.username || 'Sistem Yöneticisi';
    const currentUsername = currentUser?.username || 'admin';
    const isDeliveredOrReturned = newStatus === 'TeslimEdildi' || newStatus === 'IadeEdildi';

    const updated = tickets.map(t => {
      if (t.id === ticketId) {
        return {
          ...t,
          status: newStatus,
          updatedAt: nowISO,
          lastUpdatedByUser: currentUsername,
          lastUpdatedByName: currentUserName,
          lastUpdatedAt: nowISO,
          deliveredByUser: isDeliveredOrReturned ? (t.deliveredByUser || currentUsername) : t.deliveredByUser,
          deliveredByName: isDeliveredOrReturned ? (t.deliveredByName || currentUserName) : t.deliveredByName,
          deliveredAt: isDeliveredOrReturned ? (t.deliveredAt || nowISO) : t.deliveredAt,
          deliveryDate:
            newStatus === 'TeslimEdildi' || newStatus === 'Hazir' || newStatus === 'IadeEdildi'
              ? nowISO
              : t.deliveryDate || nowISO,
        };
      }
      return t;
    });
    handleUpdateTickets(updated);

    if (currentTicket && oldStatus !== newStatus) {
      const updatedTicketObj = {
        ...currentTicket,
        status: newStatus,
        updatedAt: nowISO,
        lastUpdatedByUser: currentUsername,
        lastUpdatedByName: currentUserName,
        lastUpdatedAt: nowISO,
        deliveredByUser: isDeliveredOrReturned ? (currentTicket.deliveredByUser || currentUsername) : currentTicket.deliveredByUser,
        deliveredByName: isDeliveredOrReturned ? (currentTicket.deliveredByName || currentUserName) : currentTicket.deliveredByName,
        deliveredAt: isDeliveredOrReturned ? (currentTicket.deliveredAt || nowISO) : currentTicket.deliveredAt,
      };
      if (newStatus === 'MusteriOnayi') {
        triggerAutoSmsIfEnabled('ticketApprovalWaiting', updatedTicketObj, companyInfo).catch(err => {
          console.error('Onay SMS gönderim hatası:', err);
        });
      } else if (newStatus === 'Hazir') {
        triggerAutoSmsIfEnabled('ticketReady', updatedTicketObj, companyInfo).catch(err => {
          console.error('Hazır SMS gönderim hatası:', err);
        });
      } else if (newStatus === 'TeslimEdildi') {
        triggerAutoSmsIfEnabled('ticketDelivered', updatedTicketObj, companyInfo).catch(err => {
          console.error('Teslim SMS gönderim hatası:', err);
        });
      }
    }
  };

  // Device Types update
  const handleSaveDeviceTypes = (newTypes: string[]) => {
    setDeviceTypes(newTypes);
    saveDeviceTypes(newTypes);
  };

  // Device Brands update
  const handleSaveDeviceBrands = (newBrands: string[]) => {
    setDeviceBrands(newBrands);
    saveDeviceBrands(newBrands);
  };

  // Company Settings update
  const handleSaveCompanyInfo = (info: CompanyInfo) => {
    setCompanyInfo(info);
    saveCompanyInfo(info);
  };

  // Reset demo data
  const handleResetData = () => {
    if (confirm('Varsayılan verileri yüklemek ve yapılan tüm değişiklikleri sıfırlamak istiyor musunuz?')) {
      resetToDefaults();
      setTickets(getStoredTickets());
      setCompanyInfo(getStoredCompanyInfo());
      setInventory(getStoredInventory());
      setStockMovements(getStoredStockMovements());
    }
  };

  // Update Customer Info across all tickets matching old identifier
  const handleUpdateCustomerInfo = (
    oldIdentifier: string,
    updated: { name: string; phone: string; email: string; address: string }
  ) => {
    const cleanOldKey = oldIdentifier.replace(/\s+/g, '').toLowerCase().trim();
    const updatedTickets = tickets.map(t => {
      const phoneClean = t.customerPhone ? t.customerPhone.replace(/\s+/g, '').toLowerCase().trim() : '';
      const nameClean = t.customerName.toLowerCase().trim();
      if (phoneClean === cleanOldKey || nameClean === cleanOldKey) {
        return {
          ...t,
          customerName: updated.name,
          customerPhone: updated.phone,
          customerEmail: updated.email,
          customerAddress: updated.address,
          updatedAt: new Date().toISOString(),
        };
      }
      return t;
    });
    handleUpdateTickets(updatedTickets);
  };

  // Trigger Print Intake
  const handlePrintIntake = (t: ServiceTicket) => {
    setPrintTicket(t);
    setPrintMode('intake');
    setIsPrintModalOpen(true);
  };

  // Trigger Print Invoice
  const handlePrintInvoice = (t: ServiceTicket) => {
    setPrintTicket(t);
    setPrintMode('invoice');
    setIsPrintModalOpen(true);
  };

  // Trigger Print Label
  const handlePrintLabel = (t: ServiceTicket) => {
    setPrintTicket(t);
    setPrintMode('label');
    setIsPrintModalOpen(true);
  };

  // Logout handler
  const handleLogout = () => {
    fetch('/api.php?action=logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {});
    clearAuthSession();
    clearSensitiveLocalCache();
    setCurrentUser(null);
    setCurrentUserState(null);
    setIsAuthenticated(false);
    setSessionExpiredMessage('');
    setIsSettingsOpen(false);
    setIsDetailModalOpen(false);
    setIsNewModalOpen(false);
    setIsPrintModalOpen(false);
    setIsSmsModalOpen(false);
    setIsDeviceTypesModalOpen(false);
    setSelectedTicket(null);
    setPrintTicket(null);
    setSmsTicket(null);
  };

  // Check online connection before opening new ticket modal to guarantee ticket ID consistency
  const handleOpenNewTicket = async () => {
    setIsCheckingConnection(true);
    try {
      // 1. First fetch latest data to sync all tickets
      const serverData = await fetchServerData();
      const authFlagStillPresent =
        sessionStorage.getItem('ts_auth') === 'true' ||
        localStorage.getItem('ts_auth_remember') === 'true';
      if (!serverData && !authFlagStillPresent) {
        setIsAuthenticated(false);
        setCurrentUser(null);
        setCurrentUserState(null);
        setSessionExpiredMessage('Oturumunuz sona erdi. Lütfen tekrar giriş yapınız.');
      }
      if (serverData?.tickets) {
        setTickets(serverData.tickets);
        if (serverData.company) setCompanyInfo(serverData.company);
        if (serverData.deviceTypes) setDeviceTypes(serverData.deviceTypes);
        setIsNewModalOpen(true);
        setIsOfflineAlertOpen(false);
        return;
      }

      // 2. Check connection endpoint
      const isOnline = await checkServerConnection();
      if (isOnline) {
        setIsNewModalOpen(true);
        setIsOfflineAlertOpen(false);
      } else {
        setIsOfflineAlertOpen(true);
      }
    } catch {
      setIsOfflineAlertOpen(true);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  // Soft-delete edilmiş servis formları SQL'de korunur fakat normal sistem ekranlarında gösterilmez.
  const userVisibleTickets = tickets.filter(t => !t.isDeleted);
  const adminHiddenTickets = isAdmin ? tickets.filter(t => t.isDeleted) : [];

  // Direct Customer Tracking Route View (Standalone link /takip, ?sayfa=takip, etc.)
  if (isTrackingRoute) {
    return (
      <CustomerTrackingView
        companyInfo={companyInfo}
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <LoginScreen
        companyInfo={companyInfo}
        sessionExpiredMessage={sessionExpiredMessage}
        onLoginSuccess={(version?: string, user?: AppUser) => {
          if (version) {
            setCurrentAuthVersion(version);
          }
          const validUser = user || getCurrentUser();
          if (validUser) {
            setCurrentUser(validUser);
            setCurrentUserState(validUser);
          }
          sessionStorage.setItem('ts_auth', 'true');
          setIsAuthenticated(true);
          setSessionExpiredMessage('');
          setIsSettingsOpen(false);
          setIsDetailModalOpen(false);
          setIsNewModalOpen(false);
          setIsPrintModalOpen(false);
          setIsSmsModalOpen(false);
          setIsDeviceTypesModalOpen(false);
          setSelectedTicket(null);
          setPrintTicket(null);
          setSmsTicket(null);
        }}
        onSaveCompanyInfo={handleSaveCompanyInfo}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 font-sans flex flex-col">
      {/* App Navigation Header */}
      <Header
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenNewTicket={handleOpenNewTicket}
        onOpenSettings={() => {
          if (isAdmin) {
            setIsSettingsOpen(true);
          }
        }}
        onResetData={handleResetData}
        onRefreshData={loadLatestData}
        onLogout={handleLogout}
        isSyncing={isSyncing || isCheckingConnection}
        companyInfo={companyInfo}
        tickets={userVisibleTickets}
        inventory={inventory}
        currentUser={currentUser}
      />

      {/* Main Content View Container */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-6 lg:px-8 py-3.5 sm:py-6 pb-28 md:pb-8">
        {activeTab === 'dashboard' && (
          <DashboardView
            tickets={userVisibleTickets}
            companyInfo={companyInfo}
            deviceTypes={deviceTypes}
            currentUser={currentUser}
            onSelectTicket={t => {
              setSelectedTicket(t);
              setIsDetailModalOpen(true);
            }}
            onOpenNewTicket={handleOpenNewTicket}
            onPrintIntake={handlePrintIntake}
            onPrintInvoice={handlePrintInvoice}
            onQuickUpdateStatus={handleQuickUpdateStatus}
            onOpenSms={t => {
              setSmsTicket(t);
              setIsSmsModalOpen(true);
            }}
          />
        )}

        {activeTab === 'history' && (
          <HistoryView
            tickets={userVisibleTickets}
            onSelectTicket={t => {
              setSelectedTicket(t);
              setIsDetailModalOpen(true);
            }}
            onUpdateTickets={handleUpdateTickets}
            isAdmin={isAdmin}
            currentUser={currentUser}
          />
        )}


        {activeTab === 'archive' && isAdmin && (
          <HiddenArchiveView
            tickets={adminHiddenTickets}
            onSelectTicket={t => {
              setSelectedTicket(t);
              setIsDetailModalOpen(true);
            }}
            onRestoreTicket={handleRestoreTicket}
          />
        )}

        {activeTab === 'inventory' && !isLimitedTechnician && (
          <InventoryView
            inventory={inventory}
            categories={inventoryCategories}
            stockMovements={stockMovements}
            onUpdateInventory={handleUpdateInventory}
            onUpdateCategories={handleUpdateInventoryCategories}
            onUpdateStockMovements={handleUpdateStockMovements}
            companyInfo={companyInfo}
            isTechnician={!isAdmin}
          />
        )}

        {activeTab === 'reports' && (
          <ReportsView
            tickets={userVisibleTickets}
            companyInfo={companyInfo}
            inventory={inventory}
            stockMovements={stockMovements}
            currentUser={currentUser}
            onSaveCompanyInfo={handleSaveCompanyInfo}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-4 text-center text-xs text-slate-500">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>{companyInfo.name} — Teknik Servis & Müşteri Takip Programı</span>
          <span>Sürüm 2.6 • {userVisibleTickets.length} Kayıtlı Servis İşlemi</span>
        </div>
      </footer>

      {/* Modals */}
      {isNewModalOpen && (
        <NewTicketModal
          isOpen={isNewModalOpen}
          onClose={() => setIsNewModalOpen(false)}
          onSubmit={handleCreateTicket}
          existingTickets={tickets}
          companyInfo={companyInfo}
          deviceTypes={deviceTypes}
          deviceBrands={deviceBrands}
          onOpenDeviceTypesModal={() => setIsDeviceTypesModalOpen(true)}
          onPrintIntake={handlePrintIntake}
          onUpdateCustomerInfo={handleUpdateCustomerInfo}
          currentUser={currentUser}
        />
      )}

      {isDetailModalOpen && selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedTicket(null);
          }}
          onSave={handleSaveTicket}
          onDeleteTicket={handleDeleteTicket}
          allTickets={tickets}
          onSelectTicket={setSelectedTicket}
          companyInfo={companyInfo}
          onPrintIntake={handlePrintIntake}
          onPrintInvoice={handlePrintInvoice}
          onPrintLabel={handlePrintLabel}
          onOpenSms={t => {
            setSmsTicket(t);
            setIsSmsModalOpen(true);
          }}
          inventory={inventory}
          deviceTypes={deviceTypes}
          isTechnician={isTechnician}
          currentUser={currentUser}
        />
      )}

      {isSmsModalOpen && smsTicket && (
        <SmsModal
          ticket={smsTicket}
          isOpen={isSmsModalOpen}
          onClose={() => {
            setIsSmsModalOpen(false);
            setSmsTicket(null);
          }}
          companyInfo={companyInfo}
          onOpenSettings={!isAdmin ? undefined : () => {
            setIsSmsModalOpen(false);
            setIsSettingsOpen(true);
          }}
          isTechnician={!isAdmin}
        />
      )}

      {isPrintModalOpen && printTicket && (
        <PrintTicketModal
          ticket={printTicket}
          mode={printMode}
          isOpen={isPrintModalOpen}
          onClose={() => {
            setIsPrintModalOpen(false);
            setPrintTicket(null);
          }}
          companyInfo={companyInfo}
        />
      )}

      {isSettingsOpen && isAdmin && (
        <CompanySettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          companyInfo={companyInfo}
          onSave={handleSaveCompanyInfo}
          deviceTypes={deviceTypes}
          onSaveDeviceTypes={handleSaveDeviceTypes}
          deviceBrands={deviceBrands}
          onSaveDeviceBrands={handleSaveDeviceBrands}
          onOpenDeviceTypesModal={() => {
            setIsSettingsOpen(false);
            setIsDeviceTypesModalOpen(true);
          }}
        />
      )}

      {isDeviceTypesModalOpen && (
        <ManageDeviceTypesModal
          isOpen={isDeviceTypesModalOpen}
          onClose={() => setIsDeviceTypesModalOpen(false)}
          deviceTypes={deviceTypes}
          onSave={handleSaveDeviceTypes}
        />
      )}

      {/* Real-time Toast Notification when new ticket arrives in MusteriOnayi (Limited technician only) */}
      {isLimitedTechnician && approvalAlertTicket && (
        <div className="fixed bottom-5 right-5 z-50 max-w-md w-full bg-slate-900 text-white rounded-2xl p-4 shadow-2xl border border-orange-500/40 animate-in slide-in-from-bottom-5 duration-300 flex flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-orange-500/20 text-orange-400 flex items-center justify-center font-bold text-lg border border-orange-500/30 shrink-0">
                🔔
              </div>
              <div>
                <span className="text-[10px] font-bold text-orange-400 uppercase tracking-wider block">Yeni Onay Bildirimi</span>
                <h4 className="text-sm font-bold text-white">Müşteri Onayı Bekleyen Cihaz Düştü!</h4>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setApprovalAlertTicket(null)}
              className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="bg-slate-800/80 rounded-xl p-2.5 border border-slate-700 text-xs space-y-1">
            <div className="flex items-center justify-between text-slate-300">
              <span className="font-mono text-orange-300 font-bold">{approvalAlertTicket.id}</span>
              <span className="text-slate-400 font-mono">{formatPhoneDisplay(approvalAlertTicket.customerPhone)}</span>
            </div>
            <div className="font-semibold text-white">{approvalAlertTicket.customerName}</div>
            <div className="text-slate-400 text-[11px]">{approvalAlertTicket.deviceBrand} {approvalAlertTicket.deviceModel}</div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setSelectedTicket(approvalAlertTicket);
                setIsDetailModalOpen(true);
                setApprovalAlertTicket(null);
              }}
              className="flex-1 py-2 px-3 bg-orange-600 hover:bg-orange-500 text-white font-bold text-xs rounded-xl shadow-sm transition-colors text-center cursor-pointer"
            >
              Cihazı Aç & İncele
            </button>
            <button
              type="button"
              onClick={() => setApprovalAlertTicket(null)}
              className="py-2 px-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-xs rounded-xl transition-colors cursor-pointer"
            >
              Kapat
            </button>
          </div>
        </div>
      )}

      {/* Offline Alert Modal: Müşteriye verilecek form numarasının değişmemesi ve çakışmaması için bağlantı zorunluluğu */}
      {isOfflineAlertOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-rose-200 text-center relative overflow-hidden">
            <div className="w-16 h-16 bg-rose-100 text-rose-600 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-rose-200 shadow-sm">
              <WifiOff className="w-8 h-8" />
            </div>

            <h3 className="text-lg font-bold text-slate-900 mb-2">
              İnternet / Sunucu Bağlantısı Yok
            </h3>

            <p className="text-sm text-slate-600 mb-6 leading-relaxed">
              Müşteriye teslim edilecek servis form numarasının çakışmaması ve numaranın sonradan değişmemesi için yeni kayıt açabilmek adına aktif internet / sunucu bağlantısı gereklidir.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-left text-xs text-amber-800 mb-6 flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span>
                Lütfen internet bağlantınızı kontrol edip <strong>Yeniden Dene</strong> butonuna tıklayınız.
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => setIsOfflineAlertOpen(false)}
                className="flex-1 py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={handleOpenNewTicket}
                disabled={isCheckingConnection}
                className="flex-1 py-2.5 px-4 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-xl shadow-md shadow-blue-500/20 transition-all flex items-center justify-center space-x-2"
              >
                {isCheckingConnection ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Kontrol Ediliyor...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>Yeniden Dene</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
